import { useEffect, useState } from "react";
import { KeyRound, LogOut, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { slugify, MENU_TABS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLOW = {
  pickup: ["placed", "confirmed", "baking", "ready", "completed"],
  delivery: ["placed", "confirmed", "baking", "out_for_delivery", "completed"],
};
const LABELS = {
  placed: "Order received",
  confirmed: "Confirmed",
  baking: "In the oven",
  out_for_delivery: "Out for delivery",
  ready: "Ready for pickup",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function OwnerPanel() {
  const [key, setKey] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bake, setBake] = useState(null);
  const [bakeDraft, setBakeDraft] = useState({});
  const [savingBake, setSavingBake] = useState(false);
  const [payLogs, setPayLogs] = useState([]);
  const [prods, setProds] = useState(null);
  const [prodDraft, setProdDraft] = useState({});
  const [savingProds, setSavingProds] = useState(false);
  const [newProd, setNewProd] = useState({ name: "", price: "", category: "viennoiseries" });
  const [addingProd, setAddingProd] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState("send"); // send | enter
  const [ownerOtp, setOwnerOtp] = useState("");
  const [newOwnerKey, setNewOwnerKey] = useState("");
  const [forgotCooldown, setForgotCooldown] = useState(0);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const load = async (k) => {
    const theKey = k !== undefined ? k : key;
    setBusy(true);
    try {
      const r = await fetch(`${API}/owner/orders`, { headers: { "X-Owner-Key": theKey } });
      if (r.status === 401) {
        setErr("Wrong key — try again.");
        sessionStorage.removeItem("fp_owner_key");
        setUnlocked(false);
        return;
      }
      if (r.status === 429) {
        setErr("Too many attempts — locked for a few minutes.");
        return;
      }
      if (!r.ok) throw new Error("bad response");
      const d = await r.json();
      setOrders(d.orders || []);
      setUnlocked(true);
      setErr("");
      setKey(theKey);
      sessionStorage.setItem("fp_owner_key", theKey);
      fetch(`${API}/bake`, { headers: { "X-Owner-Key": theKey } })
        .then((r) => r.json())
        .then((b) => {
          setBake(b);
          setBakeDraft(b.bake || {});
        })
        .catch(() => {});
      fetch(`${API}/owner/payment-logs`, { headers: { "X-Owner-Key": theKey } })
        .then((r) => r.json())
        .then((d) => setPayLogs(d.logs || []))
        .catch(() => {});
      fetch(`${API}/products`)
        .then((r) => r.json())
        .then((d) => {
          setProds(d.products || []);
          const draft = {};
          (d.products || []).forEach((p) => {
            draft[p.name] = { price: String(p.price), available: p.available !== false };
          });
          setProdDraft(draft);
        })
        .catch(() => {});
    } catch {
      setErr("Could not load orders — please try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("fp_owner_key");
    if (saved) {
      setKey(saved);
      load(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (o, s) => {
    setBusyId(o.id);
    try {
      const r = await fetch(`${API}/orders/${o.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Owner-Key": key },
        body: JSON.stringify({ status: s }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "failed");
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...d } : x)));
      toast.success(`Order #${o.id.slice(0, 8).toUpperCase()} — ${LABELS[s]}${d.whatsapp ? " · buyer pinged on WhatsApp" : ""}`);
    } catch (e) {
      toast.error(e && e.message && e.message !== "failed" ? e.message : "Could not update status.");
    } finally {
      setBusyId(null);
    }
  };

  const saveBake = async () => {
    setSavingBake(true);
    try {
      const items = {};
      for (const [k, v] of Object.entries(bakeDraft)) {
        if (v === "" || v === null || v === undefined) continue;
        const n = parseInt(v, 10);
        if (!isNaN(n)) items[k] = n;
      }
      const r = await fetch(`${API}/bake`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Owner-Key": key },
        body: JSON.stringify({ items }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "failed");
      setBake((prev) => ({ ...prev, date: d.date, bake: d.bake, left: d.left }));
      setBakeDraft(d.bake || {});
      toast.success("Tomorrow's bake saved — buyers now see live stock.");
    } catch (e) {
      toast.error(e && e.message && e.message !== "failed" ? e.message : "Could not save bake.");
    } finally {
      setSavingBake(false);
    }
  };

  const applyProducts = (list) => {
    setProds(list);
    const draft = {};
    list.forEach((p) => {
      draft[p.name] = { price: String(p.price), available: p.available !== false };
    });
    setProdDraft(draft);
  };

  const saveProducts = async () => {
    setSavingProds(true);
    try {
      const changed = prods.filter((p) => {
        const d = prodDraft[p.name];
        return d && (parseInt(d.price, 10) !== p.price || (d.available === false) !== (p.available === false));
      });
      for (const p of changed) {
        const d = prodDraft[p.name];
        const r = await fetch(`${API}/owner/products`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Owner-Key": key },
          body: JSON.stringify({ name: p.name, price: parseInt(d.price, 10) || p.price, category: p.category, available: d.available !== false }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || `Could not save ${p.name}`);
        }
      }
      if (changed.length > 0) toast.success(`Menu updated — ${changed.length} change${changed.length > 1 ? "s" : ""} live on the website.`);
      else toast.info("No changes to save.");
      const r = await fetch(`${API}/products`);
      const d = await r.json();
      applyProducts(d.products || []);
    } catch (e) {
      toast.error(typeof e.message === "string" && e.message !== "failed" ? e.message : "Could not save menu changes.");
    } finally {
      setSavingProds(false);
    }
  };

  const addProduct = async () => {
    const price = parseInt(newProd.price, 10);
    if (!newProd.name.trim() || newProd.name.trim().length < 2) {
      toast.error("Give the product a name (at least 2 letters).");
      return;
    }
    if (!price || price < 1) {
      toast.error("Enter a valid price in rupees.");
      return;
    }
    setAddingProd(true);
    try {
      const r = await fetch(`${API}/owner/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Owner-Key": key },
        body: JSON.stringify({ name: newProd.name.trim(), price, category: newProd.category, available: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "failed");
      applyProducts(d.products || []);
      setNewProd({ name: "", price: "", category: newProd.category });
      toast.success(`"${newProd.name.trim()}" added — it's on the website menu now.`);
    } catch (e) {
      toast.error(e.message && e.message !== "failed" ? e.message : "Could not add product.");
    } finally {
      setAddingProd(false);
    }
  };

  const removeProduct = async (name) => {
    try {
      const r = await fetch(`${API}/owner/products?name=${encodeURIComponent(name)}`, {
        method: "DELETE",
        headers: { "X-Owner-Key": key },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "failed");
      applyProducts(d.products || []);
      toast.success(`"${name}" removed from your menu.`);
    } catch {
      toast.error("Could not remove the product.");
    }
  };

  const sendOwnerCode = async () => {
    setForgotBusy(true);
    setForgotMsg("");
    try {
      const r = await fetch(`${API}/owner/key/forgot`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "Could not send the code.");
      setForgotStep("enter");
      setOwnerOtp("");
      setNewOwnerKey("");
      setForgotCooldown(60);
      toast.success("Reset code sent to bemina@frenchpie.co.in — check your inbox.");
    } catch (e) {
      setForgotMsg(e && e.message ? e.message : "Could not send the code.");
    } finally {
      setForgotBusy(false);
    }
  };

  const saveOwnerKey = async () => {
    setForgotBusy(true);
    setForgotMsg("");
    try {
      const r = await fetch(`${API}/owner/key/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp: ownerOtp, new_key: newOwnerKey }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "Could not save the new key.");
      toast.success("New owner key saved — opening the panel with it now.");
      setForgotMode(false);
      setForgotStep("send");
      setOwnerOtp("");
      setNewOwnerKey("");
      load(newOwnerKey);
    } catch (e) {
      setForgotMsg(e && e.message ? e.message : "Could not save the new key.");
    } finally {
      setForgotBusy(false);
    }
  };

  if (!unlocked) {
    return (
      <section data-testid="owner-page" className="pt-32 pb-24 lg:pt-40">
        <div className="max-w-md mx-auto px-6">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32] mb-4">
            Maison only
          </p>
          <h1 className="font-display font-medium tracking-tight text-4xl">Owner panel</h1>
          <form
            className="mt-10"
            onSubmit={(e) => {
              e.preventDefault();
              if (key.trim()) load(key.trim());
            }}
          >
            <label className="block">
              <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                Secret key
              </span>
              <div className="flex gap-3 mt-2">
                <input
                  className="field flex-1"
                  type="password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="current-password"
                  spellCheck={false}
                  data-testid="owner-key-input"
                  placeholder="Your owner key"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="owner-unlock-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1C130E] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors disabled:opacity-60"
                >
                  <KeyRound size={15} /> {busy ? "Checking…" : "Open"}
                </button>
              </div>
            </label>
          </form>
          {err && (
            <p className="mt-4 text-sm text-red-600" data-testid="owner-error">
              {err}
            </p>
          )}
          <button
            type="button"
            onClick={() => setForgotMode((v) => !v)}
            data-testid="owner-forgot-btn"
            className="mt-5 text-sm font-semibold text-[#C85A32] hover:underline"
          >
            Forgot key?
          </button>
          {forgotMode && (
            <div className="mt-5 rounded-2xl border border-[rgba(28,19,14,0.1)] bg-[#FDFBF7] p-5" data-testid="owner-forgot-panel">
              {forgotStep === "send" ? (
                <>
                  <p className="text-sm text-[#1C130E]/70 leading-relaxed">
                    We'll email a 6-digit reset code to <strong>bemina@frenchpie.co.in</strong>. Use it to set a
                    new key — no old key needed.
                  </p>
                  <button
                    type="button"
                    onClick={sendOwnerCode}
                    disabled={forgotBusy || forgotCooldown > 0}
                    data-testid="owner-send-code-btn"
                    className="mt-4 w-full rounded-full bg-[#1C130E] text-[#FDFBF7] py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors disabled:opacity-60"
                  >
                    {forgotBusy ? "Sending…" : forgotCooldown > 0 ? `Resend in ${forgotCooldown}s` : "Email me a reset code"}
                  </button>
                </>
              ) : (
                <>
                  <label className="block">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      6-digit code
                    </span>
                    <input
                      className="field mt-2 text-center font-mono-accent text-xl tracking-[0.5em]"
                      inputMode="numeric"
                      maxLength={6}
                      data-testid="owner-otp-input"
                      placeholder="••••••"
                      value={ownerOtp}
                      onChange={(e) => setOwnerOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    />
                  </label>
                  <label className="block mt-4">
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                      New key (at least 6 characters)
                    </span>
                    <input
                      className="field mt-2"
                      data-testid="owner-new-key-input"
                      value={newOwnerKey}
                      onChange={(e) => setNewOwnerKey(e.target.value)}
                      minLength={6}
                      maxLength={40}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={saveOwnerKey}
                    disabled={forgotBusy || ownerOtp.length !== 6 || newOwnerKey.length < 6}
                    data-testid="owner-save-key-btn"
                    className="mt-4 w-full rounded-full bg-[#1C130E] text-[#FDFBF7] py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors disabled:opacity-60"
                  >
                    {forgotBusy ? "Saving…" : "Save new key"}
                  </button>
                </>
              )}
              {forgotMsg && (
                <p className="mt-3 text-sm text-red-600" data-testid="owner-forgot-error">
                  {forgotMsg}
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="owner-page" className="pt-32 pb-24 lg:pt-40">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32] mb-3">
              Maison only
            </p>
            <h1 className="font-display font-medium tracking-tight text-4xl">Today's orders</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => load()}
              data-testid="owner-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[rgba(28,19,14,0.15)] px-5 py-2.5 text-sm font-semibold hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("fp_owner_key");
                setUnlocked(false);
                setOrders([]);
                setKey("");
              }}
              data-testid="owner-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[rgba(28,19,14,0.15)] px-5 py-2.5 text-sm font-semibold hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            >
              <LogOut size={14} /> Lock
            </button>
          </div>
        </div>

        {bake && (
          <div
            className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-5"
            data-testid="bake-panel"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl" data-testid="bake-panel-title">
                  Tomorrow's bake · {bake.date}
                </h2>
                <p className="text-xs text-[#1C130E]/55 mt-1 max-w-xl leading-relaxed">
                  Set how many you'll bake of each item — leave blank for no limit. Buyers see
                  "Only X left" when stock runs to 5 or fewer, and "Sold out" at zero.
                </p>
              </div>
              <button
                onClick={saveBake}
                disabled={savingBake}
                data-testid="bake-save-btn"
                className="rounded-full bg-[#C85A32] text-[#FDFBF7] px-6 py-2.5 text-sm font-semibold hover:bg-[#a94b29] transition-colors disabled:opacity-60"
              >
                {savingBake ? "Saving…" : "Save bake"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
              {bake.items.map((it) => {
                const slug = slugify(it.name);
                const limited = bake.left && bake.left[it.name] !== undefined;
                return (
                  <label key={it.name} className="block">
                    <span className="block text-[11px] font-medium text-[#1C130E]/70 truncate">{it.name}</span>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      data-testid={`bake-input-${slug}`}
                      placeholder="No limit"
                      value={bakeDraft[it.name] ?? ""}
                      onChange={(e) => setBakeDraft((d) => ({ ...d, [it.name]: e.target.value }))}
                      className="field mt-1"
                    />
                    <span className="block text-[10px] text-[#1C130E]/45 mt-1">
                      {limited ? `${bake.left[it.name]} left` : "no limit set"}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {prods && (
          <div
            className="mt-8 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-5"
            data-testid="products-panel"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl" data-testid="products-panel-title">
                  Menu &amp; prices
                </h2>
                <p className="text-xs text-[#1C130E]/55 mt-1 max-w-xl leading-relaxed">
                  Change a price, take an item off the menu for the day, or add something new — the
                  website menu updates the moment you save.
                </p>
              </div>
              <button
                onClick={saveProducts}
                disabled={savingProds}
                data-testid="products-save-btn"
                className="rounded-full bg-[#C85A32] text-[#FDFBF7] px-6 py-2.5 text-sm font-semibold hover:bg-[#a94b29] transition-colors disabled:opacity-60"
              >
                {savingProds ? "Saving…" : "Save changes"}
              </button>
            </div>
            <div className="mt-5 space-y-2" data-testid="products-list">
              {prods.map((p) => {
                const slug = slugify(p.name);
                const d = prodDraft[p.name] || { price: String(p.price), available: true };
                return (
                  <div
                    key={p.name}
                    data-testid="product-row"
                    className="flex items-center gap-3 flex-wrap rounded-xl border border-[rgba(28,19,14,0.08)] px-3.5 py-2.5 text-sm"
                  >
                    <span className="font-medium min-w-[200px] flex items-center gap-2">
                      {p.name}
                      {p.custom && (
                        <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-[#C85A32]/10 text-[#C85A32] border border-[#C85A32]/30">
                          New
                        </span>
                      )}
                    </span>
                    <label className="flex items-center gap-1.5 text-xs text-[#1C130E]/70">
                      <input
                        type="checkbox"
                        checked={d.available}
                        onChange={(e) =>
                          setProdDraft((s) => ({ ...s, [p.name]: { ...d, available: e.target.checked } }))
                        }
                        data-testid={`product-available-${slug}`}
                        className="accent-[#C85A32]"
                      />
                      On menu
                    </label>
                    <label className="flex items-center gap-1.5 ml-auto text-xs text-[#1C130E]/70">
                      ₹
                      <input
                        type="number"
                        min="1"
                        max="99999"
                        value={d.price}
                        onChange={(e) =>
                          setProdDraft((s) => ({ ...s, [p.name]: { ...d, price: e.target.value } }))
                        }
                        data-testid={`product-price-${slug}`}
                        className="field w-20"
                      />
                    </label>
                    {p.custom ? (
                      <button
                        onClick={() => removeProduct(p.name)}
                        data-testid={`product-remove-${slug}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    ) : p.overridden ? (
                      <button
                        onClick={() => removeProduct(p.name)}
                        data-testid={`product-reset-${slug}`}
                        className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border border-[rgba(28,19,14,0.15)] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
                      >
                        Reset
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div
              className="mt-4 flex items-end gap-3 flex-wrap border-t border-[rgba(28,19,14,0.08)] pt-4"
              data-testid="product-add-form"
            >
              <label className="block flex-1 min-w-[180px]">
                <span className="block text-[11px] font-medium text-[#1C130E]/70">New product name</span>
                <input
                  value={newProd.name}
                  onChange={(e) => setNewProd((n) => ({ ...n, name: e.target.value }))}
                  placeholder="e.g. Opera Cake"
                  data-testid="product-new-name"
                  className="field mt-1"
                />
              </label>
              <label className="block w-28">
                <span className="block text-[11px] font-medium text-[#1C130E]/70">Price ₹</span>
                <input
                  type="number"
                  min="1"
                  value={newProd.price}
                  onChange={(e) => setNewProd((n) => ({ ...n, price: e.target.value }))}
                  placeholder="320"
                  data-testid="product-new-price"
                  className="field mt-1"
                />
              </label>
              <label className="block w-44">
                <span className="block text-[11px] font-medium text-[#1C130E]/70">Menu section</span>
                <select
                  value={newProd.category}
                  onChange={(e) => setNewProd((n) => ({ ...n, category: e.target.value }))}
                  data-testid="product-new-category"
                  className="field mt-1"
                >
                  {MENU_TABS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={addProduct}
                disabled={addingProd}
                data-testid="product-add-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#C85A32] text-[#C85A32] px-5 py-2.5 text-sm font-semibold hover:bg-[#C85A32] hover:text-[#FDFBF7] transition-colors disabled:opacity-60"
              >
                <Plus size={14} /> {addingProd ? "Adding…" : "Add product"}
              </button>
            </div>
          </div>
        )}
        {payLogs.length > 0 && (
          <div
            className="mt-8 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-5"
            data-testid="paylogs-panel"
          >
            <h2 className="font-display text-2xl">Payment logs · Paytm &amp; Easebuzz</h2>
            <p className="text-xs text-[#1C130E]/55 mt-1">
              Every checkout attempt, in order — newest first. Kept for 30 days.
            </p>
            <div className="mt-4 space-y-2" data-testid="paylogs-list">
              {payLogs.map((log, i) => (
                <div
                  key={log.at + String(i)}
                  data-testid="paylog-row"
                  className="flex items-center gap-3 flex-wrap rounded-xl border border-[rgba(28,19,14,0.08)] px-3.5 py-2.5 text-sm"
                >
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      log.gateway === "paytm"
                        ? "bg-[#00B9F1]/10 text-[#0090c9] border border-[#00B9F1]/30"
                        : log.gateway === "cashfree"
                          ? "bg-[#D63847]/10 text-[#D63847] border border-[#D63847]/30"
                          : "bg-[#6C4CF1]/10 text-[#6C4CF1] border border-[#6C4CF1]/30"
                    }`}
                  >
                    {log.gateway}
                  </span>
                  <span className={`text-xs font-semibold ${log.status === "ok" ? "text-green-700" : log.status === "pending" ? "text-[#C85A32]" : "text-red-600"}`}>
                    {log.event} · {log.status}
                  </span>
                  {log.order_id && (
                    <span className="font-mono-accent text-[10px] text-[#1C130E]/45">
                      #{log.order_id.slice(0, 8).toUpperCase()}
                    </span>
                  )}
                  {log.amount && <span className="font-mono-accent text-xs">₹{log.amount}</span>}
                  {log.detail && <span className="text-xs text-[#1C130E]/50 truncate max-w-[280px]">{log.detail}</span>}
                  <span className="ml-auto text-[10px] text-[#1C130E]/40 font-mono-accent">
                    {new Date(log.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-10 space-y-4" data-testid="owner-orders-list">
          {orders.length === 0 && (
            <p className="text-[#1C130E]/60" data-testid="owner-empty">
              No orders yet — they'll appear here the moment a buyer checks out.
            </p>
          )}
          {orders.map((o) => {
            const flow = FLOW[o.fulfillment] || FLOW.pickup;
            const idx = flow.indexOf(o.status);
            const nexts = idx === -1 || o.status === "cancelled" ? [] : flow.slice(idx + 1);
            const done = o.status === "completed" || o.status === "cancelled";
            return (
              <div
                key={o.id}
                data-testid="owner-order-card"
                className="rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-4"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#1C130E]/45">
                      #{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    <p className="font-display text-lg mt-1">
                      {o.customer_name} · <a href={`tel:${o.phone}`} className="text-[#C85A32] hover:underline">{o.phone}</a>
                    </p>
                    <p className="text-sm text-[#1C130E]/70 mt-1">
                      {o.items.map((i) => `${i.qty} × ${i.name}`).join(", ")} — ₹{o.total}
                    </p>
                    <p className="text-xs text-[#1C130E]/55 mt-1">
                      {o.fulfillment === "delivery"
                        ? `Home delivery${o.delivery_address ? ` · ${o.delivery_address.line}, ${o.delivery_address.city} - ${o.delivery_address.pincode}` : ""}${o.delivery_time ? ` · within ${o.delivery_time}` : ""}`
                        : "Store pickup"}
                      {" · "}
                      {o.payment_method === "online" ? `Paid online via ${o.gateway || "gateway"} (${o.payment_status})` : "Cash"}
                      {o.note ? ` · Note: ${o.note}` : ""}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                      o.status === "cancelled"
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : done
                          ? "bg-[#1C130E] text-[#FDFBF7]"
                          : "bg-[#C85A32]/10 text-[#C85A32] border border-[#C85A32]/30"
                    }`}
                    data-testid="owner-order-status"
                  >
                    {LABELS[o.status] || o.status}
                  </span>
                </div>
                {nexts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[rgba(28,19,14,0.08)]">
                    {nexts.map((s) => (
                      <button
                        key={s}
                        disabled={busyId === o.id}
                        onClick={() => setStatus(o, s)}
                        data-testid={`owner-status-${s}-btn`}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                          s === "confirmed"
                            ? "bg-[#C85A32] text-[#FDFBF7] hover:bg-[#a94b29]"
                            : "border border-[rgba(28,19,14,0.15)] hover:border-[#C85A32] hover:text-[#C85A32]"
                        }`}
                      >
                        Mark {LABELS[s]}
                      </button>
                    ))}
                    <button
                      disabled={busyId === o.id}
                      onClick={() => setStatus(o, "cancelled")}
                      data-testid="owner-status-cancelled-btn"
                      className="rounded-full px-4 py-2 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Cancel order
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
